import React, { useState, useEffect, useCallback, useRef } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const DateSelection = ({ handleStartDate, handleEndDate, handleDateChange }) => {
  const [dateRange, setDateRange] = useState([new Date('09/01/23'), new Date()]);
  const [startDate, endDate] = dateRange;
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [activeOption, setActiveOption] = useState('');
  const [tempDateRange, setTempDateRange] = useState([]);
  const shouldTriggerDateChange = useRef(false);

  // Effect to update parent component with new start and end dates
  useEffect(() => {
    if (startDate instanceof Date) handleStartDate(new Date(startDate.getTime()));
    if (endDate instanceof Date) handleEndDate(new Date(endDate.getTime()));
  }, [startDate, endDate]);

  // Effect to trigger date change only when necessary
  useEffect(() => {
    if (shouldTriggerDateChange.current && startDate instanceof Date && endDate instanceof Date) {
      handleDateChange(new Date(startDate.getTime()), new Date(endDate.getTime()));
      shouldTriggerDateChange.current = false;
    }
  }, [startDate, endDate]);

  const handleQuickSelect = (option) => {
    let end = new Date();
    let start = new Date();

    switch (option) {
      case 'tillToday':
        start = new Date(2023,0,1); // Beginning of time
        break;
      case 'last7Days':
        start.setDate(end.getDate() - 6); // 7 days including today
        break;
      case 'last30Days':
        start.setDate(end.getDate() - 29); // 30 days including today
        break;
      case 'thisMonth':
        start = new Date(end.getFullYear(), end.getMonth(), 1);
        end = new Date(end.getFullYear(), end.getMonth() + 1, 0);
        break;
      case 'lastMonth':
        start = new Date(end.getFullYear(), end.getMonth() - 1, 1);
        end = new Date(end.getFullYear(), end.getMonth(), 0);
        break;
      default:
        break;
    }

    setTempDateRange([new Date(start.getTime()), new Date(end.getTime())]);
    setCurrentMonth(new Date(start.getTime()));
    setActiveOption(option);
  };

  const handleApply = () => {
    setDateRange(tempDateRange);
    shouldTriggerDateChange.current = true;
    setIsOpen(false);
  };

  const toggleOpen = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setTempDateRange(dateRange);
    }
  };

  const handleDateRangeChange = (dates) => {
    const [start, end] = dates;
    setTempDateRange([
      start ? new Date(start.getTime()) : null,
      end ? new Date(end.getTime()) : null
    ]);
    setActiveOption('customRange');
  };

  const customDatePickerStyles = `
    .react-datepicker {
      border: none;
    }
    .react-datepicker__month-container {
      margin-right: 2rem;
    }
    .react-datepicker__month-container:last-child {
      margin-right: 0;
    }
    .react-datepicker__day--in-range {
      background-color: #E6F2FF;
      color: #000;
    } 
    .react-datepicker__day--range-start,
    .react-datepicker__day--range-end,
    .react-datepicker__day--selected {
      background-color: #07c4fe !important;
      color: white !important;
      border-radius: 50%;
    }
    .react-datepicker__day--keyboard-selected {
      background-color: #E6F2FF;
      color: #000;
    }
    .react-datepicker__day--keyboard-selected:hover {
      background-color: #0096FF;
    }
    .react-datepicker__day:hover {
      background-color: #E6F2FF;
    }
    .react-datepicker__header {
      background-color: transparent;
      border-bottom: none;
      padding-top: 0.5em; // Reduced from default
    }
    .react-datepicker__current-month {
      // font-size: 1.255em;
      font-size: 18px;
      margin-bottom: 0.0em;
      font-weight: 400; // Added this line to make the month label less bold
    }
    .react-datepicker__navigation {
      top: 0.7em;
    }
    .react-datepicker__navigation--previous {
      left: 7px;
    }
    .react-datepicker__navigation--next {
      right: 7px;
    }
    .react-datepicker__navigation-icon::before {
      border-color: #777;
      border-width: 2px 2px 0 0;
      height: 9px;
      width: 9px;
    }
    .react-datepicker__navigation:hover *::before {
      border-color: #0096FF;
    }
    .react-datepicker__header__dropdown {
      display: flex;
      justify-content: center;
      align-items: center;
      height: 28px; // Slightly reduced height
    }
    .react-datepicker__day-names {
      margin-top: -1.0em; // Reduced space above day names
    }
  `;

  const buttonStyles = `
    w-full bg-white border border-gray-300 rounded-md px-4 py-2 inline-flex items-center justify-between text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500
    shadow-md hover:shadow-lg transition-shadow duration-200
  `;

  const containerStyles = `
    relative w-[220px]
    @media only screen and (max-width: 850px) {
      width: 150px;
    }
    @media only screen and (max-width: 790px) {
      width: 110px;
    }
    @media only screen and (max-width: 650px) {
      width: 90px;
    }
  `;

  return (
    <div className={containerStyles}>
      <button
        onClick={toggleOpen}
        className={buttonStyles}
      >
        <span className="truncate">
          {startDate && endDate ? (
            `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`
          ) : (
            'Select Date Range'
          )}
        </span>
        <svg className="ml-2 h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      </button>

      {isOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div className="bg-black bg-opacity-50 absolute inset-0" onClick={() => setIsOpen(false)}></div>
          <div className="bg-white rounded-lg shadow-lg w-[800px] max-w-[95vw] relative">
            <div className="flex flex-col h-full">
              <div className="flex flex-grow">
                <div className="w-1/4 bg-gray-100 rounded-tl-lg pt-4">
                  {[
                    { label: 'Till Today', value: 'tillToday' },
                    { label: 'Last 7 Days', value: 'last7Days' },
                    { label: 'Last 30 Days', value: 'last30Days' },
                    { label: 'This Month', value: 'thisMonth' },
                    { label: 'Last Month', value: 'lastMonth' },
                    { label: 'Custom Range', value: 'customRange' }
                  ].map((option) => (
                    <button
                      key={option.value}
                      onClick={() => handleQuickSelect(option.value)}
                      className={`w-44 text-left px-6 py-2 transition duration-150 ease-in-out rounded-md mx-2 ${
                        activeOption === option.value 
                          ? 'bg-white text-gray-800 font-medium border border-gray-150' 
                          : 'hover:bg-gray-200 text-gray-600'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
                <div className="w-3/4 p-4 flex justify-center">
                  <style>{customDatePickerStyles}</style>
                  <div className="flex">
                    <DatePicker
                      selected={tempDateRange[0]}
                      onChange={handleDateRangeChange}
                      startDate={tempDateRange[0]}
                      endDate={tempDateRange[1]}
                      selectsRange
                      inline
                      monthsShown={2}
                      calendarStartDate={currentMonth}
                    />
                  </div>
                </div>
              </div>
              <div className="flex justify-between items-center px-4 py-2 border-t">
                <div className="flex-grow text-right text-sm text-gray-600 pr-4">
                  {tempDateRange[0] && tempDateRange[1] ? `${tempDateRange[0].toLocaleDateString()} - ${tempDateRange[1].toLocaleDateString()}` : 'No date range selected'}
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setIsOpen(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleApply}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    Apply
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(DateSelection);
